"use client";

import { useQuery } from "@tanstack/react-query";
import { useEffect } from "react";

import { attentionCounts, getAttentionCountsQueryKey } from "@/lib/api/generated";
import type { AttentionCountsParams } from "@/lib/api/model";

import { useConversationsStore } from "../store/conversations-store";

export type CountScope =
  | { project_id: string }
  | { job_id: string }
  | { job_candidate_id: string };

function toParams(scope: CountScope): AttentionCountsParams {
  if ("project_id" in scope) return { project_id: scope.project_id };
  if ("job_id" in scope) return { job_id: scope.job_id };
  return { job_candidate_id: scope.job_candidate_id };
}

export function useAttentionCounts(scope: CountScope | null) {
  const set = useConversationsStore((s) => s.setAttentionCounts);
  const live = useConversationsStore((s) => s.attentionCounts);
  const liveProject = useConversationsStore((s) => s.attentionProjectId);
  const params = scope ? toParams(scope) : undefined;
  const projectId = scope && "project_id" in scope ? scope.project_id : null;
  const q = useQuery({
    queryKey: getAttentionCountsQueryKey(params),
    enabled: scope !== null,
    queryFn: () => attentionCounts(params),
    staleTime: 30_000,
  });
  useEffect(() => {
    if (q.data && projectId) set(projectId, q.data.groups);
  }, [q.data, q.dataUpdatedAt, projectId, set]);
  const groups =
    projectId && live && liveProject === projectId
      ? live
      : (q.data?.groups ?? null);
  const total = groups ? Object.values(groups).reduce((a, b) => a + b, 0) : 0;
  return { groups, total, isLoading: q.isLoading };
}
